/**
 * Splide Auto Accessibility
 * Automatically adds accessibility features to all Splide loop instances
 * Optimized for performance and cross-browser compatibility
 */

(function() {
  'use strict';

  // 포커스 가능한 요소 선택자 (캐싱)
  const FOCUSABLE_SELECTOR = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

  // requestAnimationFrame fallback for older browsers
  const raf = window.requestAnimationFrame || 
              window.webkitRequestAnimationFrame || 
              window.mozRequestAnimationFrame || 
              function(callback) { return setTimeout(callback, 16); };
  
  const cancelAnimationFrame = window.cancelAnimationFrame || 
                                window.webkitCancelAnimationFrame || 
                                window.mozCancelAnimationFrame || 
                                clearTimeout;

  // inert 설정/제거 헬퍼 함수 (성능 최적화)
  function setInert(element, value) {
    try {
      if ('inert' in element) {
        element.inert = value;
      } else {
        if (value) {
          element.setAttribute('inert', '');
        } else {
          element.removeAttribute('inert');
        }
      }
    } catch (e) {
      if (value) {
        element.setAttribute('inert', '');
      } else {
        element.removeAttribute('inert');
      }
    }
  }

  function initAccessibility() {
    const splides = document.querySelectorAll('.splide');
    
    // forEach 대신 for loop (IE11 호환)
    for (let i = 0; i < splides.length; i++) {
      const splide = splides[i];
      
      // 이미 처리된 경우 스킵
      if (splide._accessibilityEnabled) continue;
      
      // loop mode인지 확인 (clone 슬라이드가 있으면 loop)
      const hasClones = splide.querySelector('.splide__slide--clone');
      
      if (!hasClones) {
        continue;
      }
      
      splide._accessibilityEnabled = true;
      
      // 초기 처리
      updateSlidesAccessibility(splide);
      
      // aria-hidden 속성 변경 감지
      let rafId;
      const observer = new MutationObserver(function() {
        try {
          // 이미 예약된 실행이 있으면 취소 (중복 방지)
          if (rafId) {
            cancelAnimationFrame(rafId);
          }
          // 다음 브라우저 렌더링 프레임에 실행 (최적화)
          rafId = raf(function() {
            updateSlidesAccessibility(splide);
            rafId = null;
          });
        } catch (error) {
          console.error('Splide observer error:', error);
        }
      });
      
      observer.observe(splide, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
        subtree: true
      });
    }
  }

  function updateSlidesAccessibility(container) {
    try {
      // 현재 포커스된 요소 확인 (한 번만)
      const activeElement = document.activeElement;
      
      // Hidden slides: inert + tabindex + blur
      const hiddenSlides = container.querySelectorAll('.splide__slide[aria-hidden="true"]');
      for (let i = 0; i < hiddenSlides.length; i++) {
        const slide = hiddenSlides[i];
        
        // 포커스된 요소가 이 슬라이드 안에 있으면 blur
        if (activeElement && slide.contains(activeElement)) {
          activeElement.blur();
        }
        
        // inert 설정 (이미 설정되어 있으면 스킵 - 성능 최적화)
        if (!slide.hasAttribute('inert') && !slide.inert) {
          setInert(slide, true);
        }
        
        // tabindex 설정 (변경이 필요한 요소만)
        const focusable = slide.querySelectorAll(FOCUSABLE_SELECTOR);
        for (let j = 0; j < focusable.length; j++) {
          const el = focusable[j];
          if (el.getAttribute('tabindex') !== '-1') {
            el.setAttribute('tabindex', '-1');
            el.setAttribute('data-splide-tabindex', '');
          }
        }
      }

      // Visible slides: remove inert + tabindex
      const visibleSlides = container.querySelectorAll('.splide__slide:not([aria-hidden="true"])');
      for (let i = 0; i < visibleSlides.length; i++) {
        const slide = visibleSlides[i];
        
        // inert 제거 (설정되어 있을 때만 - 성능 최적화)
        if (slide.hasAttribute('inert') || slide.inert) {
          setInert(slide, false);
        }
        
        // tabindex 복원 (실제로 -1인 요소만)
        const focusable = slide.querySelectorAll('[tabindex="-1"][data-splide-tabindex]');
        for (let j = 0; j < focusable.length; j++) {
          focusable[j].removeAttribute('tabindex');
          focusable[j].removeAttribute('data-splide-tabindex');
        }
      }
    } catch (error) {
      console.error('Splide accessibility error:', error);
    }
  }

  // 초기 실행
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAccessibility);
  } else {
    initAccessibility();
  }

  // 늦게 로드되는 Splide 대응
  window.addEventListener('load', initAccessibility);
  
  // 동적으로 추가되는 Splide 감지 (MutationObserver 지원 확인)
  if (typeof MutationObserver !== 'undefined') {
    let debounceTimer;
    const globalObserver = new MutationObserver(function(mutations) {
      let hasSplideAdded = false;
      
      for (let i = 0; i < mutations.length; i++) {
        const mutation = mutations[i];
        
        // aria-hidden 속성 변경 감지 (즉시 blur만 처리)
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          try {
            const target = mutation.target;
            if (target.classList && target.classList.contains('splide__slide')) {
              const ariaHidden = target.getAttribute('aria-hidden');
              if (ariaHidden === 'true') {
                // aria-hidden="true"가 설정되는 순간 즉시 blur 처리
                const activeElement = document.activeElement;
                if (activeElement && target.contains(activeElement)) {
                  activeElement.blur();
                }
                // 초기화는 하지 않음 (개별 observer가 처리)
              }
            }
          } catch (error) {
            // blur 실패 시에도 계속 진행 (치명적 아님)
          }
        }
        
        // .splide 요소가 추가되었을 때만 초기화
        const addedNodes = mutation.addedNodes;
        for (let j = 0; j < addedNodes.length; j++) {
          const node = addedNodes[j];
          if (node.nodeType === 1) {
            if (node.classList && node.classList.contains('splide')) {
              hasSplideAdded = true;
              break;
            }
            if (node.querySelector && node.querySelector('.splide')) {
              hasSplideAdded = true;
              break;
            }
          }
        }
        if (hasSplideAdded) break;
      }
      
      // 새 Splide가 추가됐을 때만 initAccessibility 호출
      if (hasSplideAdded) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(initAccessibility, 200);
      }
    });
    
    // document.body가 준비되면 observe 시작
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        if (document.body) {
          globalObserver.observe(document.body, {
            childList: true,
            attributes: true,
            attributeFilter: ['aria-hidden'],
            subtree: true
          });
        }
      });
    } else if (document.body) {
      globalObserver.observe(document.body, {
        childList: true,
        attributes: true,
        attributeFilter: ['aria-hidden'],
        subtree: true
      });
    }
  }
})();
